import React, { useCallback, useRef } from 'react';
import type { ScreenComponentDef } from '@core/types/slot-screen-component.types';

interface CanvasComponentProps {
  component: ScreenComponentDef;
  isSelected: boolean;
  zoom: number;
  onSelect: (id: string) => void;
  onDoubleClick: (id: string) => void;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, width: number, height: number, x: number, y: number) => void;
}

type HandlePosition = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const HANDLE_SIZE = 8;
const MIN_SIZE = 10;

const HANDLE_POSITIONS: Record<HandlePosition, { top: string; left: string; cursor: string }> = {
  nw: { top: '-4px', left: '-4px', cursor: 'nwse-resize' },
  n:  { top: '-4px', left: 'calc(50% - 4px)', cursor: 'ns-resize' },
  ne: { top: '-4px', left: 'calc(100% - 4px)', cursor: 'nesw-resize' },
  e:  { top: 'calc(50% - 4px)', left: 'calc(100% - 4px)', cursor: 'ew-resize' },
  se: { top: 'calc(100% - 4px)', left: 'calc(100% - 4px)', cursor: 'nwse-resize' },
  s:  { top: 'calc(100% - 4px)', left: 'calc(50% - 4px)', cursor: 'ns-resize' },
  sw: { top: 'calc(100% - 4px)', left: '-4px', cursor: 'nesw-resize' },
  w:  { top: 'calc(50% - 4px)', left: '-4px', cursor: 'ew-resize' },
};

function getBorderColor(format: ScreenComponentDef['format']): string {
  switch (format) {
    case 'transparent': return 'rgba(34, 211, 238, 0.6)';  // cyan
    case 'text':        return 'rgba(250, 204, 21, 0.6)';   // yellow
    case 'opaque':
    default:            return 'rgba(161, 161, 170, 0.5)';  // gray/zinc
  }
}

function getFillColor(format: ScreenComponentDef['format']): string {
  switch (format) {
    case 'transparent': return 'rgba(34, 211, 238, 0.06)';
    case 'text':        return 'rgba(250, 204, 21, 0.06)';
    case 'opaque':
    default:            return 'rgba(161, 161, 170, 0.04)';
  }
}

function getSelectedResult(component: ScreenComponentDef) {
  if (!component.selectedResultId) return null;
  return component.generatedResults.find(
    r => r.id === component.selectedResultId && r.status === 'ready' && r.imageUrl
  ) ?? null;
}

export function CanvasComponent({
  component,
  isSelected,
  zoom,
  onSelect,
  onDoubleClick,
  onMove,
  onResize,
}: CanvasComponentProps) {
  const dragRef = useRef<{
    startX: number; startY: number;
    compX: number; compY: number;
  } | null>(null);

  const resizeRef = useRef<{
    handle: HandlePosition;
    startX: number; startY: number;
    compX: number; compY: number;
    compW: number; compH: number;
  } | null>(null);

  const selectedResult = getSelectedResult(component);

  // ---- Drag ----
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Ignore if locked or if a resize handle is being used
    if (component.locked) return;
    if ((e.target as HTMLElement).dataset.resizeHandle) return;

    e.stopPropagation();
    onSelect(component.id);

    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      compX: component.x,
      compY: component.y,
    };
  }, [component.id, component.x, component.y, component.locked, onSelect]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current) {
      const dx = (e.clientX - dragRef.current.startX) / zoom;
      const dy = (e.clientY - dragRef.current.startY) / zoom;
      onMove(
        component.id,
        Math.round(dragRef.current.compX + dx),
        Math.round(dragRef.current.compY + dy),
      );
    }

    if (resizeRef.current) {
      const dx = (e.clientX - resizeRef.current.startX) / zoom;
      const dy = (e.clientY - resizeRef.current.startY) / zoom;
      const { handle, compX, compY, compW, compH } = resizeRef.current;

      let newX = compX;
      let newY = compY;
      let newW = compW;
      let newH = compH;

      // Horizontal
      if (handle === 'nw' || handle === 'w' || handle === 'sw') {
        newX = compX + dx;
        newW = compW - dx;
      }
      if (handle === 'ne' || handle === 'e' || handle === 'se') {
        newW = compW + dx;
      }

      // Vertical
      if (handle === 'nw' || handle === 'n' || handle === 'ne') {
        newY = compY + dy;
        newH = compH - dy;
      }
      if (handle === 'sw' || handle === 's' || handle === 'se') {
        newH = compH + dy;
      }

      // Enforce minimum size
      if (newW < MIN_SIZE) {
        if (handle === 'nw' || handle === 'w' || handle === 'sw') {
          newX = compX + compW - MIN_SIZE;
        }
        newW = MIN_SIZE;
      }
      if (newH < MIN_SIZE) {
        if (handle === 'nw' || handle === 'n' || handle === 'ne') {
          newY = compY + compH - MIN_SIZE;
        }
        newH = MIN_SIZE;
      }

      onResize(
        component.id,
        Math.round(newW),
        Math.round(newH),
        Math.round(newX),
        Math.round(newY),
      );
    }
  }, [component.id, zoom, onMove, onResize]);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
    resizeRef.current = null;
  }, []);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDoubleClick(component.id);
  }, [component.id, onDoubleClick]);

  // ---- Resize handles ----
  const handleResizePointerDown = useCallback((
    e: React.PointerEvent<HTMLDivElement>,
    handle: HandlePosition,
  ) => {
    if (component.locked) return;
    e.stopPropagation();

    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);

    resizeRef.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      compX: component.x,
      compY: component.y,
      compW: component.width,
      compH: component.height,
    };
  }, [component.locked, component.x, component.y, component.width, component.height]);

  const handleResizePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Forward to the main handler which checks resizeRef
    handlePointerMove(e);
  }, [handlePointerMove]);

  const handleResizePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.releasePointerCapture(e.pointerId);
    resizeRef.current = null;
  }, []);

  if (!component.visible) return null;

  const borderColor = getBorderColor(component.format);
  const fillColor = getFillColor(component.format);

  return (
    <div
      style={{
        position: 'absolute',
        left: component.x,
        top: component.y,
        width: component.width,
        height: component.height,
        zIndex: component.zIndex,
        cursor: component.locked ? 'default' : 'move',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onDoubleClick={handleDoubleClick}
    >
      {/* Visual layer */}
      {selectedResult ? (
        /* Generated image */
        <img
          src={selectedResult.imageUrl}
          alt={component.name}
          className="w-full h-full object-cover pointer-events-none select-none"
          draggable={false}
        />
      ) : (
        /* Placeholder */
        <div
          className="w-full h-full flex items-center justify-center select-none"
          style={{
            border: `1.5px dashed ${borderColor}`,
            backgroundColor: fillColor,
            borderRadius: 2,
          }}
        >
          <span
            className="text-[10px] font-medium text-center px-1 leading-tight"
            style={{ color: borderColor, opacity: 0.9 }}
          >
            {component.name}
          </span>
        </div>
      )}

      {/* Selection ring */}
      {isSelected && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            outline: '2px solid rgba(59, 130, 246, 0.8)',
            outlineOffset: 1,
            borderRadius: 2,
          }}
        />
      )}

      {/* Resize handles (only when selected and not locked) */}
      {isSelected && !component.locked && (
        Object.entries(HANDLE_POSITIONS).map(([pos, style]) => (
          <div
            key={pos}
            data-resize-handle="true"
            style={{
              position: 'absolute',
              top: style.top,
              left: style.left,
              width: HANDLE_SIZE,
              height: HANDLE_SIZE,
              cursor: style.cursor,
              zIndex: 999,
            }}
            className="bg-blue-500 border border-white rounded-[1px]"
            onPointerDown={(e) => handleResizePointerDown(e, pos as HandlePosition)}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
          />
        ))
      )}
    </div>
  );
}
